import {
  ProductIds,
  ProgramIds,
  ApplicantTypeIds,
  professionalGradFlowsDictionary,
  clinicalPsychologyPrograms,
  dentalPrograms,
  lawPrograms,
  medicalPrograms,
  optometryPrograms,
  pharmacyPrograms,
  podiatryPrograms,
  theologyPrograms,
  veterinaryPrograms,
} from "@/config/constants";
import { computed } from "vue";
import type { AppData, Applicant, Application, State } from "@/models/application";
import { Flows } from "@/config/enums";
import type { Ref } from "vue";
import { Programs, Products } from "@/config/enums";
import { ugPrograms, dgPrograms, ApplicationStatusTypeIds } from "@/config/constants";
import store from "@/store";
import useFormatter from "@/use/formatter";

export default function useApplicationData(customText?: Ref<any>) {
	const { capitalizeFirstLetter } = useFormatter();

  const allowCoApplicants = computed<boolean>(() => !isParentLOC.value);

  const allowSchoolUndecided = computed<boolean>(() => isUGLOC.value || isParentLOC.value);

  const appData = computed<AppData>(() => {
    return store?.state?.appData;
  });

  const application = computed<Application>(() => {
    return store?.state?.appData?.application;
  });

  const applicationId = computed<number>(() => {
    return store?.state?.appData?.application?.id;
  });

  const applicants = computed<Applicant[]>(() => store.state?.appData?.applicants);

	const applicantCount = computed<number>(() => (store.state?.appData?.applicants || [])?.length);

  const coApplicant = computed<Applicant | undefined>(() =>
    store.state.appData?.applicants?.find((a: Applicant) => a.applicantTypeId === ApplicantTypeIds.CO_APPLICANT),
  );

  const coApplicantTypeDisplayName = computed<string>(() => store.getters["coApplicantTypeDisplayName"]);

  const coApplicantTypeDisplayNameInline = computed<string>(() =>
    store.getters["coApplicantTypeDisplayName"]?.toLowerCase(),
  );

  const cosignerLedCosignerDeclinedFlow = computed(() => {
    if (appData.value?.application?.applicationStatusId === ApplicationStatusTypeIds.SMR_DECLINED) {
      return true;
    }
    return false;
  });

  const creditUnionName = computed<string>(() => store?.state?.lenderData?.name);

  const customStudentType = computed<string | undefined>(() => {
    return customText?.value?.StudentType || undefined;
  });

  const degreeType = computed<string>(() => {
    if (customStudentType.value) {
      return customStudentType.value;
    }

    if (programTypeId.value === ProgramIds.UG || isValidUG.value) {
      return "undergrad";
    } else if (programTypeId.value === ProgramIds.DG) {
      return "graduate";
    } else if (isMedical.value) {
      return "medical";
    } else if (isHealthProfessionalDegree.value || isAdvancedDegree.value) {
      if (isClinicalPsychology.value) {
        return "clinical psychology";
      } else if (isDental.value) {
        return "dental";
      } else if (isLaw.value) {
        return "law";
      } else if (isPharmacy.value) {
        return "pharmacy";
      } else if (isPodiatry.value) {
        return "podiatry";
      } else if (isOptometry.value) {
        return "optometry";
      } else if (isTheology.value) {
        return "theology";
      } else if (isVeterinary.value) {
        return "veterinary";
      } else {
        return "graduate";
      }
    } else {
      return "";
    }
  });

  const emailAddress = computed<string>(() => {
    return store?.state?.lenderData && store?.state?.lenderData?.supportEmailAddress
      ? store?.state?.lenderData?.supportEmailAddress
      : "member_support@studentchoice.org";
  });

  const flowType = computed<number | null>(() => {
    const flowTypeId: number | null | undefined = store.state.appData.flowType || localStorage.getItem("flow");
    if (flowTypeId === null || flowTypeId === undefined || isNaN(Number(flowTypeId))) {
      return null;
    }

    return Number(flowTypeId);
  });

  const getCoApplicantTypeId = computed<number>(() => store.getters["coApplicantTypeId"]);

  const getPrimaryApplicantTypeId = computed<number>(() => store.getters["primaryApplicantTypeId"]);

  const hrefTFN = computed<string>(() => {
    const tfn = store.state.lenderData.tollFreeNumber;
    return store.state.lenderData && tfn
      ? tfn.startsWith("1")
        ? `+${store.state.lenderData.tollFreeNumber.replace(/-/g, "")}`
        : `+1${store.state.lenderData.tollFreeNumber.replace(/-/g, "")}`
      : "N/A";
  });

  const hasFlowType = computed<boolean>(() => {
    return flowType.value != null && flowType.value !== undefined && flowType.value > 0;
  });

  const hasRepaymentBasedRates = computed<boolean>(
    () => store.state.loanConfigurationModule?.loanOptionsConfiguration?.hasRepaymentBasedRates,
  );

  const inlineProductDisplay = computed<string>(() => {
    if (productTypeId.value === Number(Products.LOC)) {
      return "line of credit";
    }
    return "loan";
  });

  const isClinicalPsychology = computed<boolean>(() => {
    return clinicalPsychologyPrograms.includes(programTypeId.value);
  });

  const isCombineProfessionalGrad = computed<boolean>(() => {
    const programTypeIds = [
      Number(Programs.MEDICAL_AND_OSTEOPATHIC),
      Number(Programs.MEDICAL_AND_OSTEOPATHIC2),
      Number(Programs.MEDICAL_AND_OSTEOPATHIC3),
      Number(Programs.ADVANCED_DEGREE),
      Number(Programs.ADVANCED_DEGREE2),
      Number(Programs.ADVANCED_DEGREE3),
      Number(Programs.HEALTH_PROFESSIONS),
      Number(Programs.HEALTH_PROFESSIONS2),
      Number(Programs.HEALTH_PROFESSIONS3),
    ] as number[];

    return programTypeIds.includes(programTypeId.value);
  });

  const isDental = computed<boolean>(() => {
    return dentalPrograms.includes(programTypeId.value);
  });

  const isDomesticGrad = computed<boolean>(() => {
    return programTypeId.value === ProgramIds.DG || flowType.value === Number(Flows.STRICT);
  });

  const isLaw = computed<boolean>(() => {
    return lawPrograms.includes(programTypeId.value);
  });

  const isMedical = computed<boolean>(() => {
    if (programTypeId.value && programTypeId.value > 0) {
      return medicalPrograms.includes(programTypeId.value);
    }

    return flowType.value === Number(Flows.MDDO);
  });

  const isHealthProfessionalDegree = computed<boolean>(() => {
    if (programTypeId.value && programTypeId.value > 0) {
      return professionalGradFlowsDictionary[Flows.MEDICAL].includes(programTypeId.value);
    }

    return flowType.value === Number(Flows.MEDICAL);
  });

  const isNonDefaultFlow = computed<boolean>(() => {
    return flowType.value != null && flowType.value !== undefined && flowType.value > Flows.STRICT;
  });

  const isOptometry = computed<boolean>(() => {
    return optometryPrograms.includes(programTypeId.value);
  });

  const isParentLOC = computed<boolean>(
    () => productTypeId.value === ProductIds.LOC && programTypeId.value === Number(Programs.PARENT),
  );

  const isPharmacy = computed<boolean>(() => {
    return pharmacyPrograms.includes(programTypeId.value);
  });

  const isPodiatry = computed<boolean>(() => {
    return podiatryPrograms.includes(programTypeId.value);
  });

  const isAdvancedDegree = computed<boolean>(() => {
    if (programTypeId.value && programTypeId.value > 0) {
      return professionalGradFlowsDictionary[Flows.PROF].includes(programTypeId.value);
    }

    return flowType.value === Number(Flows.PROF);
  });

  const isRefi = computed<boolean>(() => productTypeId.value === ProductIds.REFI);

  const isSecondApplicant = computed<boolean>(() => {
    const routes = [
      "coborrowerInfo",
      "borrowerInfo",
      "borrowerInvitedApplicantInfo",
      "coborrowerInvitedApplicantInfo",
			"borrowerInvitedApplicantInfoContinued",
      "coborrowerInvitedApplicantInfoContinued",
      "refiBorrowerInfo",
      "refiCosignerInfo",
      "refiBorrowerInvitedApplicantInfo",
			"refiCosignerInvitedApplicantInfo",
			"refiBorrowerInvitedApplicantInfoContinued",
      "refiCosignerInvitedApplicantInfoContinued",
      "studentInformation",
    ] as string[];

    return routes.includes(store.state.appData.currentState.route);
  });

  const isSoloApp = computed<boolean>(() => {
    return !appData.value?.application?.metadata?.withCoApplicants;
  });

  const isTheology = computed<boolean>(() => {
    return theologyPrograms.includes(programTypeId.value);
  });

  const isUGLOC = computed<boolean>(
    () => programTypeId.value === ProgramIds.UG && productTypeId.value === ProductIds.LOC,
  );

  const isValidProfessionalGradFlow = computed<boolean>(() => {
    return (
      isMedical.value || isAdvancedDegree.value || isHealthProfessionalDegree.value || isCombineProfessionalGrad.value
    );
  });

  const isValidDG = computed<boolean>(() => dgPrograms.includes(programTypeId.value));

  const isValidUG = computed<boolean>(() => ugPrograms.includes(programTypeId.value));

  const isVeterinary = computed<boolean>(() => {
    return veterinaryPrograms.includes(programTypeId.value);
  });

  const lenderData = computed(() => {
    return store?.state?.lenderData;
  });

  const lenderId = computed<number>(() => store?.state?.lenderData?.lid);

  const primaryApplicant = computed<Applicant | undefined>(() =>
    store.state.appData?.applicants?.find((a: Applicant) => a.applicantTypeId === ApplicantTypeIds.PRIMARY_APPLICANT),
  );

  const primaryApplicantTypeDisplayName = computed<string>(() => store.getters["primaryApplicantTypeDisplayName"]);

  const primaryApplicantTypeDisplayNameInline = computed<string>(() =>
    store.getters["primaryApplicantTypeDisplayName"]?.toLowerCase(),
  );

  const productTypeId = computed<number>(() => appData.value?.application.productTypeId || 0);

  const productTypeName = computed<string>(() => store.state.appData?.application?.productTypeName);

  const programName = computed<string>(() => {
    const productName = lenderData.value?.program?.productName || "";

    if (customText?.value?.ProgramDisplayName) {
      return customText?.value?.ProgramDisplayName;
    }

    // Direct Link Support for Professional Grad Flows
    if (programTypeId.value && programTypeId.value > 0) {
      if (
        (isValidProfessionalGradFlow.value) &&
        degreeType.value &&
        degreeType.value?.trim()?.length > 0
      ) {
        return `${capitalizeFirstLetter(degreeType.value)} Degree ${productName}`;
      }
    }

    if (isMedical.value) {
      return `Medical Degree ${productName}`;
    } else if (isHealthProfessionalDegree.value && !isDental.value) {
      return `Health Professional ${productName}`;
    } else if (isAdvancedDegree.value) {
      return `Advanced Degree ${productName}`;
    } else {
      return store?.state?.lenderData?.program?.name || "";
    }
  });

  const programTypeId = computed<number>(() => appData.value?.application?.programTypeId || 0);

  const softPullEnabled = computed<boolean>(() => store.getters["SOFT_PULL_ENABLED_FLAG"]);

  const softPullConsentGetter = computed(() => store.state.consentsModule.softPullConsent);

  const statesSeen = computed<State[]>(() => appData.value?.statesSeen);

  const tollFreeNumber = computed<string>(() => {
    return store?.state?.lenderData && store?.state?.lenderData?.tollFreeNumber
      ? store?.state?.lenderData?.tollFreeNumber
      : store.getters["supportPhoneNumber"];
  });

  const useProfessionalGradLoanOptionsModal = computed<boolean>(() => {
    return isValidProfessionalGradFlow.value && hasRepaymentBasedRates.value;
  });

  // Methods
  function setFlowType(flowTypeId: number) {
    store.dispatch("setFlowType", flowTypeId);
  }

  return {
    allowCoApplicants,
    allowSchoolUndecided,
    appData,
		applicantCount,
    applicants,
    application,
    applicationId,
    coApplicant,
    coApplicantTypeDisplayName,
    coApplicantTypeDisplayNameInline,
    cosignerLedCosignerDeclinedFlow,
    creditUnionName,
    customText,
    degreeType,
    emailAddress,
    flowType,
    getCoApplicantTypeId,
    getPrimaryApplicantTypeId,
    hrefTFN,
    hasFlowType,
    hasRepaymentBasedRates,
    inlineProductDisplay,
    isClinicalPsychology,
    isCombineProfessionalGrad,
    isDental,
    isDomesticGrad,
    isLaw,
    isMedical,
    isHealthProfessionalDegree,
    isNonDefaultFlow,
    isOptometry,
    isParentLOC,
    isPharmacy,
    isPodiatry,
    isAdvancedDegree,
    isRefi,
    isSecondApplicant,
    isSoloApp,
    isTheology,
    isUGLOC,
    isValidProfessionalGradFlow,
    isValidDG,
    isValidUG,
    isVeterinary,
    lenderData,
    lenderId,
    primaryApplicant,
    primaryApplicantTypeDisplayName,
    primaryApplicantTypeDisplayNameInline,
    productTypeId,
    productTypeName,
    professionalGradFlowsDictionary,
    programName,
    programTypeId,
    setFlowType,
    softPullEnabled,
    softPullConsentGetter,
    statesSeen,
    tollFreeNumber,
    useProfessionalGradLoanOptionsModal,
  };
}
